<script>
import FormSelect from "@/components/ui/form/FormSelect.vue";
import StatusSelector from "@/components/parts/StatusSelector.vue";
import { useI18n } from 'vue-i18n';
import slabyteLogo from "@/assets/slabyte-logo.png";
import helpers from "@root/helpers.js";

export default {
  name: "OrderGeneralInfo",
  components: { FormSelect, StatusSelector },
  props: {
    orderData: {
      type: Object,
      required: true
    },
    orderId: {
      type: String,
      default: null
    },
    badgesOptions: {
      type: Array,
      default: () => []
    },
    tempOrders: {
      type: Array,
      default: () => []
    },
    isDistributor: {
      type: Boolean,
      default: false
    },
    currentUserRole: {
      type: String,
      default: ''
    },
    oldOrderStatus: {
      type: String,
      default: ''
    },
    isSubmitting: {
      type: Boolean,
      default: false
    },
    brandsList: {
      type: Array,
      default: () => []
    },
    modelsList: {
      type: Array,
      default: () => []
    },
    yearsList: {
      type: Array,
      default: () => []
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchBrand: '',
      searchModel: '',
      searchYear: '',
      isOpenBrand: false,
      isOpenModel: false,
      isOpenYear: false,
      openCarCard: true,
      openStatusCard: true
    }
  },
  emits: [
    'map-changes',
    'find-orders',
    'select-order',
    'send-to-cutting',
    'cutting-done',
    'clone-order',
    'update-data',
    'fetch-models',
    'fetch-years',
    'open-patterns-modal'
  ],
  setup() {
    const { t } = useI18n();
    return { t, slabyteLogo };
  },
  mounted() {
    document.addEventListener('click', this.closeAllDropdowns);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeAllDropdowns);
  },
  computed: {
    isDraftsman() {
      return this.$store.getters.isDraftsman;
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    showCuttingDoneButton() {
      return this.orderId && (this.currentUserRole === 'draftsman' || this.currentUserRole === 'admin') && this.oldOrderStatus === 'cutting';
    },
    filteredBrands() {
      return this.filterOptions(this.brandsList, this.searchBrand);
    },
    filteredModels() {
      return this.filterOptions(this.modelsList, this.searchModel);
    },
    filteredYears() {
      return this.filterOptions(this.yearsList, this.searchYear);
    },
    carColClass() {
      return this.compact ? 'col-12 col-md-6' : 'col-12 col-md-4';
    },
    assemblyPrograms() {
      return helpers.assemblyPrograms;
    },
    showAssemblyProgram() {
      return this.isDraftsman && (!this.orderData.hasNoPatterns || this.orderData.noPatternsType === 'partial');
    },
    cutPrograms() {
      const programs = (this.orderData.filmCuts || []).map(cut => cut.program).filter(Boolean);
      return [...new Set(programs)];
    }
  },
  methods: {
    handleUrgentToggle(event) {
      const isChecked = event.target.checked;

      this.updateField('isUrgent', isChecked);
      this.onMapChanges('isUrgent', isChecked);
    },
    closeAllDropdowns() {
      this.isOpenBrand = false;
      this.isOpenModel = false;
      this.isOpenYear = false;
    },
    filterOptions(list, search) {
      if (!Array.isArray(list)) return [];

      const seen = new Set();
      const unique = [];
      for (const raw of list) {
        if (raw === null || raw === undefined) continue;
        const value = String(raw);
        const key = value.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        unique.push(value);
      }

      const query = (search || '').trim().toLowerCase();
      if (!query) {
        // Без пошуку: спершу латиничні марки за абеткою, кириличні (УАЗ, ВАЗ тощо) — в кінець
        const isCyrillic = value => /[а-яёіїєґ]/i.test(value);
        return unique
          .sort((a, b) => {
            const aCyr = isCyrillic(a);
            const bCyr = isCyrillic(b);
            if (aCyr !== bCyr) return aCyr ? 1 : -1;
            return a.localeCompare(b);
          })
          .slice(0, 50);
      }

      return unique
        .filter(value => value.toLowerCase().includes(query))
        .sort((a, b) => {
          const al = a.toLowerCase();
          const bl = b.toLowerCase();
          const aStarts = al.startsWith(query);
          const bStarts = bl.startsWith(query);
          if (aStarts !== bStarts) return aStarts ? -1 : 1;
          return al.localeCompare(bl);
        })
        .slice(0, 50);
    },
    toggleDropdown(type, event) {
      if (event) event.stopPropagation();

      if (type === 'brand') {
        this.isOpenBrand = !this.isOpenBrand;
        this.isOpenModel = false;
        this.isOpenYear = false;
        if (this.isOpenBrand) this.searchBrand = '';
      } else if (type === 'model') {
        if (!this.orderData.carBrand || this.modelsList.length === 0 || this.orderData.isModelUnknown) return;
        this.isOpenModel = !this.isOpenModel;
        this.isOpenBrand = false;
        this.isOpenYear = false;
        if (this.isOpenModel) this.searchModel = '';
      } else if (type === 'year') {
        if (!this.orderData.carModel || this.yearsList.length === 0 || this.orderData.isModelUnknown) return;
        this.isOpenYear = !this.isOpenYear;
        this.isOpenBrand = false;
        this.isOpenModel = false;
        if (this.isOpenYear) this.searchYear = '';
      }
    },

    handleManualTitleToggle(event) {
      const isChecked = event.target.checked;
      this.updateField('isManualTitle', isChecked);
      this.onMapChanges('isManualTitle', isChecked);
    },

    handleProblemToggle(field, event) {
      const isChecked = event.target.checked;
      this.updateField(field, isChecked);
      this.onMapChanges(field, isChecked);
    },

    handlePatternRequestToggle(event) {
      const isChecked = event.target.checked;
      this.updateField('patternRequestSent', isChecked);
      this.onMapChanges('patternRequestSent', isChecked);
    },

    clearPatternRequest() {
      if (this.orderData.patternRequestSent) {
        this.updateField('patternRequestSent', false);
        this.onMapChanges('patternRequestSent', false);
      }
    },

    handleNoPatternsToggle(event) {
      const isChecked = event.target.checked;
      this.updateField('hasNoPatterns', isChecked);
      this.onMapChanges('hasNoPatterns', isChecked);

      if (!isChecked) {
        if (this.orderData.noPatternsType) {
          this.updateField('noPatternsType', '');
          this.onMapChanges('noPatternsType', '');
        }
        this.clearPatternRequest();
      }
    },

    handleNoPatternsTypeChange(event) {
      const value = event.target.value;
      this.updateField('noPatternsType', value);
      this.onMapChanges('noPatternsType', value);

      if (value !== 'none_in_any' && value !== 'partial') {
        this.clearPatternRequest();
      }

      if (value === 'none_in_any' || value === 'none_in_ours') {
        this.clearAssemblyProgram();
      }
    },

    toggleAssemblyProgram(value) {
      // Програму, що стоїть на відрізку, вручну прибрати не можна — вона керується з відрізків
      if (this.isAssemblyProgramLocked(value)) return;

      const current = Array.isArray(this.orderData.assemblyPrograms) ? [...this.orderData.assemblyPrograms] : [];
      const idx = current.indexOf(value);
      if (idx === -1) {
        current.push(value);
      } else {
        current.splice(idx, 1);
      }
      this.updateField('assemblyPrograms', current);
      this.onMapChanges('assemblyPrograms', current);
    },

    isAssemblyProgramSelected(value) {
      // обрана, якщо у загальному списку АБО стоїть на якомусь відрізку
      const inList = Array.isArray(this.orderData.assemblyPrograms) && this.orderData.assemblyPrograms.includes(value);
      return inList || this.cutPrograms.includes(value);
    },

    isAssemblyProgramLocked(value) {
      return this.cutPrograms.includes(value);
    },

    clearAssemblyProgram() {
      if (Array.isArray(this.orderData.assemblyPrograms) && this.orderData.assemblyPrograms.length) {
        this.updateField('assemblyPrograms', []);
        this.onMapChanges('assemblyPrograms', []);
      }
    },

    updateField(field, value) {
      this.$emit('update-data', { field, value });
    },
    onMapChanges(field, value) {
      this.$emit('map-changes', field, value);
    },
    onFindOrders(query) {
      this.$emit('find-orders', query);
    },
    onSelectOrder(order) {
      this.$emit('select-order', order);
    },
    selectBrand(brand) {
      this.updateField('carBrand', brand);
      this.onMapChanges('carBrand', brand);
      this.searchBrand = '';
      this.isOpenBrand = false;

      if (this.orderData.isModelUnknown) {
        this.updateField('isModelUnknown', false);
        this.onMapChanges('isModelUnknown', false);
      }
      this.$emit('fetch-models');
    },
    selectModel(model) {
      this.updateField('carModel', model);
      this.onMapChanges('carModel', model);
      this.searchModel = '';
      this.isOpenModel = false;
      this.$emit('fetch-years');
    },
    selectYear(year) {
      this.updateField('carYear', year);
      this.onMapChanges('carYear', year);
      this.searchYear = '';
      this.isOpenYear = false;
    },
    cloneOrder() {
      this.$emit('clone-order');
    }
  }
}
</script>

<template>
  <div class="col-12">
    <div class="row g-3">
    <div class="col-12" v-if="orderData.humanId">
      <h4 class="py-2 mb-0 d-flex align-items-center flex-wrap gap-2">
        {{ t('headings.orderData') }}
        <span> ID: <strong>{{ orderData.humanId }}</strong> </span>
        <button v-if="isDistributor || isAdmin"
                type="button"
                :class="{ 'loading': isSubmitting }"
                class="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 ms-2"
                @click="cloneOrder"
                :disabled="isSubmitting">
          <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-copy" width="18" height="18" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M8 8m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z" />
            <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2" />
          </svg>
          {{ t('buttons.clone') }}
        </button>
        <span v-if="orderData.wasCut && (orderData.status === 'done' || orderData.status === 'closed')" class="badge d-inline-flex align-items-center gap-1 bg-info text-dark ms-2" style="font-size: 0.8rem;">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M9.15 14.85l8.85 -10.85" />
            <path d="M7 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
            <path d="M17 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
            <path d="M9.15 9.15l8.85 10.85" />
          </svg>
          {{ t('log.fields.wasCutBadge', 'Порізане') }}
        </span>
      </h4>
    </div>

    <div class="col-12">
      <div class="card shadow-sm border-0 section-card">
        <div class="card-header bg-light d-flex align-items-center justify-content-between"
             @click="openCarCard = !openCarCard" style="cursor: pointer; user-select: none;">
          <span class="fw-semibold d-flex align-items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
              <path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
              <path d="M5 17h-2v-6l2 -5h9l4 5h1a2 2 0 0 1 2 2v4h-2m-4 0h-6m-6 -6h15m-6 0v-5" />
            </svg>
            {{ t('headings.carData', 'Автомобіль') }}
          </span>
          <svg :class="{'collapse-arrow-rotated': !openCarCard}" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted" style="transition: transform 0.2s ease;">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M6 9l6 6l6 -6" />
          </svg>
        </div>
        <div :class="{ 'collapse': true, 'show': openCarCard }">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-12">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" role="switch" id="manualTitleToggle"
                       :checked="orderData.isManualTitle"
                       @change="handleManualTitleToggle">
                <label class="form-check-label text-primary fw-bold" for="manualTitleToggle">
                  {{ t('labels.manualTitle') }}
                </label>
              </div>
            </div>

            <template v-if="!orderData.isManualTitle">
              <div :class="carColClass">
                <label class="form-label small text-muted mb-1">{{ t('placeholders.selectBrand', 'Оберіть марку') }}</label>
                <div class="input-group">
                  <div class="dropdown flex-grow-1" @click.stop>
                    <button class="btn btn-outline-secondary w-100 text-start d-flex justify-content-between align-items-center bg-white text-dark"
                            type="button"
                            @click="toggleDropdown('brand', $event)" style="border-color: #dee2e6;">
                      <span>{{ orderData.carBrand || t('placeholders.selectBrand', 'Оберіть марку') }}</span>
                      <span class="dropdown-toggle"></span>
                    </button>

                    <ul class="dropdown-menu w-100 shadow-sm p-0" :class="{ 'show': isOpenBrand }">
                      <li class="p-2 border-bottom">
                        <input type="text" class="form-control form-control-sm" :placeholder="t('placeholders.search', 'Пошук...')" v-model="searchBrand" @click.stop>
                      </li>
                      <div style="max-height: 250px; overflow-y: auto;">
                        <li v-for="brand in filteredBrands" :key="brand">
                          <a class="dropdown-item py-2" href="#" @click.prevent="selectBrand(brand)">{{ brand }}</a>
                        </li>
                        <li v-if="filteredBrands.length === 0">
                          <span class="dropdown-item text-muted">{{ t('messages.nothingFound', 'Нічого не знайдено') }}</span>
                        </li>
                      </div>
                    </ul>
                  </div>
                </div>
              </div>

              <div :class="carColClass">
                <label class="form-label small text-muted mb-1">{{ t('placeholders.selectModel') }}</label>
                <div class="dropdown w-100" @click.stop>
                  <button class="btn btn-outline-secondary w-100 text-start d-flex justify-content-between align-items-center bg-white text-dark"
                          type="button"
                          @click="toggleDropdown('model', $event)"
                          style="border-color: #dee2e6;"
                          :disabled="!orderData.carBrand || modelsList.length === 0 || orderData.isModelUnknown">
                    <span :class="{'text-muted': orderData.isModelUnknown}">
                       {{ orderData.isModelUnknown ? t('labels.unknown') : (orderData.carModel || t('placeholders.selectModel')) }}
                    </span>
                    <span class="dropdown-toggle"></span>
                  </button>
                  <ul class="dropdown-menu w-100 shadow-sm p-0" :class="{ 'show': isOpenModel }">
                    <li class="p-2 border-bottom">
                      <input type="text" class="form-control form-control-sm" :placeholder="t('placeholders.search')" v-model="searchModel" @click.stop>
                    </li>
                    <div style="max-height: 250px; overflow-y: auto;">
                      <li v-for="model in filteredModels" :key="model">
                        <a class="dropdown-item py-2" href="#" @click.prevent="selectModel(model)">{{ model }}</a>
                      </li>
                      <li v-if="filteredModels.length === 0">
                        <span class="dropdown-item text-muted">{{ t('messages.nothingFound') }}</span>
                      </li>
                    </div>
                  </ul>
                </div>
              </div>

              <div :class="carColClass">
                <label class="form-label small text-muted mb-1">{{ t('placeholders.selectYear') }}</label>
                <div class="dropdown w-100" @click.stop>
                  <button class="btn btn-outline-secondary w-100 text-start d-flex justify-content-between align-items-center bg-white text-dark"
                          type="button"
                          @click="toggleDropdown('year', $event)"
                          style="border-color: #dee2e6;"
                          :disabled="!orderData.carModel || yearsList.length === 0 || orderData.isModelUnknown">
                    <span :class="{'text-muted': orderData.isModelUnknown}">
                       {{ orderData.isModelUnknown ? t('labels.unknown') : (orderData.carYear || t('placeholders.selectYear')) }}
                    </span>
                    <span class="dropdown-toggle"></span>
                  </button>
                  <ul class="dropdown-menu w-100 shadow-sm p-0" :class="{ 'show': isOpenYear }">
                    <li class="p-2 border-bottom">
                      <input type="text" class="form-control form-control-sm" :placeholder="t('placeholders.search')" v-model="searchYear" @click.stop>
                    </li>
                    <div style="max-height: 250px; overflow-y: auto;">
                      <li v-for="year in filteredYears" :key="year">
                        <a class="dropdown-item py-2" href="#" @click.prevent="selectYear(year)">{{ year }}</a>
                      </li>
                      <li v-if="filteredYears.length === 0">
                        <span class="dropdown-item text-muted">{{ t('messages.nothingFound') }}</span>
                      </li>
                    </div>
                  </ul>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="col-12">
                <label class="form-label small text-muted mb-1">{{ t('placeholders.carBrandModel') }}</label>
                <div class="input-group">
                  <span class="input-group-text">@</span>
                  <textarea
                      class="form-control"
                      rows="2"
                      :placeholder="t('placeholders.carBrandModel')"
                      :value="orderData.title"
                      @input="[updateField('title', $event.target.value), onFindOrders($event.target.value)]"
                      @change="onMapChanges('title', $event.target.value)"
                  ></textarea>
                </div>
              </div>
            </template>

            <div class="col-12" v-if="tempOrders.length > 0">
              <div class="form-group search-orders d-block">
                <ul class="list-group">
                  <li v-for="order in tempOrders" :key="order._id" :class="{
                      'list-group-item': true,
                      'list-group-item-action': orderData.title !== order.title,
                      'active': orderData.title === order.title
                    }" @click="onSelectOrder(order)">
                    {{ order.title }}
                  </li>
                </ul>
              </div>
            </div>

            <div class="col-12 col-md-6">
              <label class="form-label small text-muted mb-1">{{ t('labels.badge') }}</label>
              <FormSelect
                  id="badgeSelect"
                  :modelValue="orderData.badgeId"
                  :options="badgesOptions"
                  :placeholder="t('labels.badge')"
                  @update:modelValue="[updateField('badgeId', $event), onMapChanges('badgeId', $event)]"
              />
            </div>

            <div class="col-12 d-flex align-items-end" v-if="orderData.carBrand && orderData.carModel && orderData.carYear && orderId && isDraftsman">
              <button
                  type="button"
                  class="btn btn-primary w-100 d-flex align-items-center justify-content-center py-2"
                  @click="$emit('open-patterns-modal')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-2">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M4 7V4a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
                  <path d="M16 7V4a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
                  <path d="M4 17v-3a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
                  <path d="M16 17v-3a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
                </svg>
                {{ t('buttons.selectPatterns', 'Вибрати лекала з бази') }}
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>

    <div class="col-12" v-if="orderId || !isDistributor">
      <div class="card shadow-sm border-0 section-card">
        <div class="card-header bg-light d-flex align-items-center justify-content-between"
             @click="openStatusCard = !openStatusCard" style="cursor: pointer; user-select: none;">
          <span class="fw-semibold d-flex align-items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
              <path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />
              <path d="M9 12l2 2l4 -4" />
            </svg>
            {{ t('headings.statusSection', 'Статус та наявність лекал') }}
          </span>
          <svg :class="{'collapse-arrow-rotated': !openStatusCard}" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted" style="transition: transform 0.2s ease;">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M6 9l6 6l6 -6" />
          </svg>
        </div>
        <div :class="{ 'collapse': true, 'show': openStatusCard }">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-12 col-lg-5" v-if="orderId">
              <StatusSelector
                  :modelValue="orderData.status"
                  @update:modelValue="updateField('status', $event)"
                  @mapChanges="onMapChanges"
              />
            </div>

            <div class="col-12" :class="orderId ? 'col-lg-7' : 'col-lg-12'" v-if="!isDistributor">
              <div class="no-patterns-block h-100" :class="{ 'is-active': orderData.hasNoPatterns }">
                <div class="np-header">
                  <div class="form-check form-switch m-0">
                    <input class="form-check-input" type="checkbox" role="switch"
                           id="hasNoPatterns"
                           :checked="orderData.hasNoPatterns"
                           @change="handleNoPatternsToggle">
                    <label class="form-check-label fw-bold d-flex align-items-center gap-2" for="hasNoPatterns">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="np-icon">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M3 3l18 18" />
                        <path d="M4 7v-2a1 1 0 0 1 1 -1h2" />
                        <path d="M4 17v2a1 1 0 0 0 1 1h2" />
                        <path d="M16 4h3a1 1 0 0 1 1 1v3" />
                        <path d="M20 16v3a1 1 0 0 1 -1 1h-3" />
                      </svg>
                      {{ t('labels.hasNoPatterns', 'Немає лекал') }}
                    </label>
                  </div>
                </div>

                <div v-if="orderData.hasNoPatterns" class="np-body">
                  <select
                      class="form-select"
                      :class="{ 'is-invalid': !orderData.noPatternsType }"
                      id="noPatternsType"
                      :value="orderData.noPatternsType"
                      @change="handleNoPatternsTypeChange">
                    <option value="" disabled>{{ t('labels.noPatternsSelectStatus', 'Оберіть статус відсутності лекал') }}</option>
                    <option value="none_in_any">{{ t('labels.noPatternsNoneInAny', 'Немає лекал в жодній з баз') }}</option>
                    <option value="none_in_ours">{{ t('labels.noPatternsNoneInOurs', 'Немає лекал в наших базах') }}</option>
                    <option value="partial">{{ t('labels.noPatternsPartial', 'Є лекала частково') }}</option>
                  </select>
                  <small v-if="!orderData.noPatternsType" class="text-danger d-block mt-1">
                    {{ t('messages.selectNoPatternsStatus', 'Будь ласка, оберіть статус відсутності лекал.') }}
                  </small>

                  <div v-if="orderData.noPatternsType === 'none_in_any' || orderData.noPatternsType === 'partial'"
                       class="pattern-request-block"
                       :class="{ 'is-active': orderData.patternRequestSent }">
                    <div class="form-check m-0">
                      <input class="form-check-input" type="checkbox"
                             id="patternRequestSent"
                             :checked="orderData.patternRequestSent"
                             @change="handlePatternRequestToggle">
                      <label class="form-check-label fw-bold" for="patternRequestSent">
                        {{ t('labels.patternRequestSent', 'Відправлено запит на отримання лекал') }}
                      </label>
                    </div>
                    <a href="https://slabyte.com" target="_blank" rel="noopener" class="pr-brand" title="SLAbyte">
                      <img :src="slabyteLogo" alt="SLAbyte" class="pr-brand-logo">
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12" v-if="showAssemblyProgram">
              <div class="assembly-program-block" :class="{ 'is-active': orderData.assemblyPrograms && orderData.assemblyPrograms.length }">
                <label class="form-label fw-bold d-flex align-items-center gap-2 mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ap-icon">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M4 7V4a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
                    <path d="M16 7V4a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
                    <path d="M4 17v-3a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
                    <path d="M16 17v-3a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
                  </svg>
                  {{ t('labels.assemblyProgram', 'Програма збірки лекала') }}
                </label>
                <div class="assembly-program-options d-flex flex-wrap gap-2">
                  <button
                      v-for="program in assemblyPrograms"
                      :key="program.value"
                      type="button"
                      class="ap-chip"
                      :class="{ 'is-selected': isAssemblyProgramSelected(program.value), 'is-locked': isAssemblyProgramLocked(program.value) }"
                      :title="isAssemblyProgramLocked(program.value) ? t('labels.assemblyProgramLocked', 'Обрано на відрізку — змінюється у відрізках') : ''"
                      @click="toggleAssemblyProgram(program.value)">
                    {{ program.label }}
                    <svg v-if="isAssemblyProgramLocked(program.value)" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ap-lock">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z" />
                      <path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" />
                      <path d="M8 11v-4a4 4 0 1 1 8 0v4" />
                    </svg>
                  </button>
                </div>
                <small class="text-muted d-block mt-2">
                  {{ t('labels.assemblyProgramHint', 'Можна обрати кілька. Програми з відрізків підтягуються автоматично й керуються у відрізках.') }}
                </small>
              </div>
            </div>

            <div class="col-12" v-if="orderId && isDistributor && oldOrderStatus === 'done'">
              <button
                  type="button"
                  :class="{ 'loading': isSubmitting }"
                  class="btn btn-success text-white"
                  @click="$emit('send-to-cutting')"
                  :disabled="isSubmitting">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-cut me-1">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M7 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/>
                  <path d="M17 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/>
                  <path d="M9.15 14.85l8.85 -10.85"/>
                  <path d="M9.15 9.15l8.85 10.85"/>
                </svg>
                {{ t('buttons.sendToCutting') }}
              </button>
            </div>

            <div class="col-12" v-if="orderId && (currentUserRole === 'draftsman' || currentUserRole === 'admin') && oldOrderStatus === 'cutting'">
              <button
                  type="button"
                  :class="{ 'loading': isSubmitting }"
                  class="btn btn-success text-white"
                  @click="$emit('cutting-done')"
                  :disabled="isSubmitting">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-circle-check">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"/>
                  <path d="M9 12l2 2l4 -4"/>
                </svg>
                {{ t('buttons.cuttingDone') }}
              </button>
            </div>

            <div class="col-12" v-if="!isDistributor">
              <hr class="my-1 text-muted">
              <div class="flag-tiles">
                <label class="flag-tile" :class="{ 'is-active': orderData.isUrgent }">
                  <input class="form-check-input" type="checkbox"
                         :checked="orderData.isUrgent"
                         @change="handleUrgentToggle">
                  <span class="flag-tile-text">{{ t('messages.isUrgent') }}</span>
                </label>

                <template v-if="orderId && orderData.status !== 'active' && orderData.status !== 'new'">
                  <label class="flag-tile flag-tile-danger"
                         :class="{ 'is-active': orderData.hasProblematicPattern, 'is-disabled': orderData.hasProblematicPattern && currentUserRole !== 'admin' }">
                    <input class="form-check-input"
                           type="checkbox"
                           :checked="orderData.hasProblematicPattern"
                           :disabled="orderData.hasProblematicPattern && currentUserRole !== 'admin'"
                           @change="handleProblemToggle('hasProblematicPattern', $event)">
                    <span class="flag-tile-text">{{ t('labels.problemPattern', 'Проблемна викройка') }}</span>
                  </label>

                  <label class="flag-tile flag-tile-danger"
                         :class="{ 'is-active': orderData.hasProblematicPackaging, 'is-disabled': orderData.hasProblematicPackaging && currentUserRole !== 'admin' }">
                    <input class="form-check-input"
                           type="checkbox"
                           :checked="orderData.hasProblematicPackaging"
                           :disabled="orderData.hasProblematicPackaging && currentUserRole !== 'admin'"
                           @change="handleProblemToggle('hasProblematicPackaging', $event)">
                    <span class="flag-tile-text">{{ t('labels.problemPackaging', 'Проблема упаковки') }}</span>
                  </label>
                </template>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>

    <div class="col-12">
      <div class="input-group">
        <span class="input-group-text">{{ t('tableheadings.notes') }}</span>
        <textarea
            class="form-control"
            rows="3"
            :value="orderData.description"
            @input="updateField('description', $event.target.value)"
            @change="onMapChanges('description', $event.target.value)"
            :placeholder="t('placeholders.orderDescription')"
        ></textarea>
      </div>
    </div>
  </div>
  </div>
</template>

<style scoped lang="scss">
.form-check-input.problem-checkbox:checked {
  background-color: #dc3545;
  border-color: #dc3545;
}
.form-check-input.problem-checkbox:focus {
  box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
}

.section-card {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;

  .card-header {
    border-bottom: 1px solid #e9ecef;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    font-size: 0.95rem;
  }
}

.collapse-arrow-rotated {
  transform: rotate(-90deg);
}

.no-patterns-block {
  border: 1px dashed #ced4da;
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
  background: #fcfcfd;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  .np-icon {
    color: #adb5bd;
    transition: color 0.2s ease;
  }

  &.is-active {
    border-style: solid;
    border-color: #fd7e14;
    background: #fff8f1;
    box-shadow: 0 0 0 0.2rem rgba(253, 126, 20, 0.12);

    .np-header .form-check-label {
      color: #d9650a;
    }

    .np-icon {
      color: #fd7e14;
    }
  }

  .np-body {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(253, 126, 20, 0.25);
  }

  .np-header .form-check-input,
  .np-header .form-check-label {
    cursor: pointer;
  }

  .form-check-input:checked {
    background-color: #fd7e14;
    border-color: #fd7e14;
  }
}

.assembly-program-block {
  border: 1px dashed #ced4da;
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
  background: #fcfcfd;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  .ap-icon {
    color: #adb5bd;
    transition: color 0.2s ease;
  }

  .form-label {
    color: #495057;
  }

  &.is-active {
    border-style: solid;
    border-color: #0d6efd;
    background: #eef4ff;
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.12);

    .form-label {
      color: #0a58ca;
    }

    .ap-icon {
      color: #0d6efd;
    }
  }

  .ap-chip {
    border: 1px solid #ced4da;
    background: #fff;
    color: #495057;
    border-radius: 999px;
    padding: 0.3rem 0.85rem;
    font-size: 0.85rem;
    line-height: 1.2;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: #0d6efd;
      color: #0a58ca;
    }

    &.is-selected {
      border-color: #0d6efd;
      background: #0d6efd;
      color: #fff;
      box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.15);
    }

    // Замкнена (взята з відрізка): виглядає обраною, але не клікабельна для зняття
    &.is-locked {
      cursor: default;
      border-color: #0a58ca;
      background: #0a58ca;
      color: #fff;

      &:hover {
        border-color: #0a58ca;
        color: #fff;
      }

      .ap-lock {
        margin-left: 0.35rem;
        opacity: 0.85;
        vertical-align: -1px;
      }
    }
  }
}

.pattern-request-block {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  flex-wrap: nowrap;
  margin-top: 0.75rem;
  border: 1px dashed #f7b27a;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #fff7f0;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  .form-check {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
  }

  .form-check-input,
  .form-check-label {
    cursor: pointer;
  }

  .form-check-label {
    font-size: 0.8rem;
    line-height: 1.25;
  }

  .form-check-input:checked {
    background-color: #f15a24;
    border-color: #f15a24;
  }

  &.is-active {
    border-style: solid;
    border-color: #f15a24;
    background: #fff0e6;
    box-shadow: 0 0 0 0.2rem rgba(241, 90, 36, 0.12);

    .form-check-label {
      color: #d24a14;
    }
  }

  .pr-brand {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    line-height: 0;
    padding: 0.25rem 0.6rem;
    border-radius: 8px;
    background: #1f2733;
    text-decoration: none;
    transition: background-color 0.15s ease, transform 0.15s ease;

    &:hover {
      background: #2a3543;
      transform: translateY(-1px);
    }

    .pr-brand-logo {
      height: 16px;
      width: auto;
      object-fit: contain;
    }
  }
}

.flag-tiles {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.flag-tile {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 0;
  padding: 0.5rem 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  background: #fff;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

  .form-check-input {
    margin: 0;
    flex-shrink: 0;
    cursor: pointer;
    float: none;
  }

  .flag-tile-text {
    font-size: 0.875rem;
    line-height: 1.2;
    white-space: nowrap;
  }

  &:hover {
    border-color: #adb5bd;
    background: #f8f9fa;
  }

  &.is-active {
    border-color: #0d6efd;
    background: #eef4ff;
    box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.12);

    .flag-tile-text {
      color: #0a58ca;
      font-weight: 600;
    }

    .form-check-input:checked {
      background-color: #0d6efd;
      border-color: #0d6efd;
    }
  }

  &.flag-tile-danger {
    .flag-tile-text {
      color: #dc3545;
      font-weight: 600;
    }

    &.is-active {
      border-color: #dc3545;
      background: #fdecee;
      box-shadow: 0 0 0 0.15rem rgba(220, 53, 69, 0.12);

      .flag-tile-text {
        color: #b02a37;
      }

      .form-check-input:checked {
        background-color: #dc3545;
        border-color: #dc3545;
      }
    }
  }

  &.is-disabled {
    opacity: 0.7;
    cursor: not-allowed;

    .form-check-input {
      cursor: not-allowed;
    }
  }
}

.search-orders {
  max-height: 200px;
  overflow-y: scroll;
}

.loading {
  position: relative;
  &::before {
    content: "";
    position: absolute;
    left: 0; top: 0; width: 100%; height: 100%;
    background: #fff; opacity: 0.5; z-index: 100;
  }
  &::after {
    content: "";
    position: absolute;
    left: 0; top: 0; width: 100%; height: 100%;
    background-position: center;
    background-size: 100px;
    background-repeat: no-repeat;
    background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwcHgiICBoZWlnaHQ9IjIwMHB4IiAgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCIgcHJlc2VydmVBc3BlY3RSYXRpbz0ieE1pZFlNaWQiIGNsYXNzPSJsZHMtcmlwcGxlIiBzdHlsZT0iYmFja2dyb3VuZDogbm9uZTsiPjxjaXJjbGUgY3g9IjUwIiBjeT0iNTAiIHI9IjYuMTM4NDciIGZpbGw9Im5vbmUiIG5nLWF0dHItc3Ryb2tlPSJ7e2NvbmZpZy5jMX19IiBuZy1hdHRyLXN0cm9rZS13aWR0aD0ie3tjb25maWcud2lkdGh9fSIgc3Ryb2tlPSIjMzM3YWI3IiBzdHJva2Utd2lkdGg9IjIiPjxhbmltYXRlIGF0dHJpYnV0ZU5hbWU9InIiIGNhbGNNb2RlPSJzcGxpbmUiIHZhbHVlcz0iMDs0MCIga2V5VGltZXM9IjA7MSIgZHVyPSIxIiBrZXlTcGxpbmVzPSIwIDAuMiAwLjggMSIgYmVnaW49Ii0wLjVzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSI+PC9hbmltYXRlPjxhbmltYXRlIGF0dHJpYnV0ZU5hbWU9Im9wYWNpdHkiIGNhbGNNb2RlPSJzcGxpbmUiIHZhbHVlcz0iMTswIiBrZXlUaW1lcz0iMDsxIiBkdXI9IjEiIGtleVNwbGluZXM9IjAuMiAwIDAuOCAxIiBiZWdpbj0iLTAuNXMiIHJlcGVhdENvdW50PSJpbmRlZmluaXRlIj48L2FuaW1hdGU+PC9jaXJjbGU+PGNpcmNsZSBjeD0iNTAiIGN5PSI1MCIgcj0iMjguMzk3NSIgZmlsbD0ibm9uZSIgbmctYXR0ci1zdHJva2U9Int7Y29uZmlnLmMyfX0iIG5nLWF0dHItc3Ryb2tlLXdpZHRoPSJ7e2NvbmZpZy53aWR0aH19IiBzdHJva2U9IiM1YmMwZGUiIHN0cm9rZS13aWR0aD0iMiI+PGFuaW1hdGUgYXR0cmlidXRlTmFtZT0iciIgY2FsY01vZGU9InNwbGluZSIgdmFsdWVzPSIwOzQwIiBrZXlUaW1lcz0iMDsxIiBkdXI9IjEiIGtleVNwbGluZXM9IjAgMC4yIDAuOCAxIiBiZWdpbj0iMHMiIHJlcGVhdENvdW50PSJpbmRlZmluaXRlIj48L2FuaW1hdGU+PGFuaW1hdGUgYXR0cmlidXRlTmFtZT0ib3BhY2l0eSIgY2FsY01vZGU9InNwbGluZSIgdmFsdWVzPSIxOzAiIGtleVRpbWVzPSIwOzEiIGR1cj0iMSIga2V5U3BsaW5lcz0iMC4yIDAgMC44IDEiIGJlZ2luPSIwcyIgcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiPjwvYW5pbWF0ZT48L2NpcmNsZT48L3N2Zz4=);
    z-index: 101;
  }
}
</style>